import Alert from "@cloudscape-design/components/alert";
import { captureReferralCode } from "../../lib/referral";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Checkbox from "@cloudscape-design/components/checkbox";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Link from "@cloudscape-design/components/link";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { useEffect, useMemo, useState } from "react";
import { api, errorMessage, writeToken } from "../../api/client";
import { usePublicConfig } from "../../api/queries";
import type { AffiliateReferrer, LoginResponse, MessageResponse } from "../../api/types";
import { useValidatedForm } from "../../lib/useValidatedForm";
import {
  confirmationRules,
  emailRules,
  EMAIL_CONSTRAINT,
  nameRules,
  passwordRules,
  PASSWORD_CONSTRAINT,
  type ValidationConfig,
} from "../../lib/validation";
import { navigate, useFollow } from "../../router/useRoute";
import { PublicShell } from "./PublicShell";
import { GoogleSignInButton } from "../../components/GoogleSignInButton";
import { ResultPage } from "./ResultPage";
import { useTurnstile } from "./useTurnstile";

type Field = "name" | "email" | "password" | "passwordConfirm";

const FIELD_ORDER = ["name", "email", "password", "passwordConfirm"] as const satisfies readonly Field[];

export function RegisterPage() {
  const follow = useFollow();
  const { data: publicConfig } = usePublicConfig();
  const turnstile = useTurnstile(publicConfig);
  // Mã có thể đến từ `?ref=` ngay lúc này, hoặc từ cookie của lần bấm link đối
  // tác trước đó trong vòng 30 ngày.
  const [referralCode] = useState(captureReferralCode);
  const [referrer, setReferrer] = useState<AffiliateReferrer | null>(null);

  useEffect(() => {
    if (!referralCode) return;
    let active = true;
    // Xác nhận mã có thật **trước** khi khách điền form. Gõ xong mọi thứ rồi mới
    // biết link giới thiệu đã hỏng là chỗ mất khách đắt nhất trong luồng này.
    void api<{ referrer: AffiliateReferrer }>(`/affiliate/lookup/${encodeURIComponent(referralCode)}`)
      .then((result) => active && setReferrer(result.referrer))
      .catch(() => active && setReferrer(null));
    return () => {
      active = false;
    };
  }, [referralCode]);

  const [values, setValues] = useState({ password: "" });
  const config = useMemo<ValidationConfig>(
    () => ({
      name: nameRules,
      email: emailRules,
      password: passwordRules,
      passwordConfirm: confirmationRules(() => values.password),
    }),
    [values.password],
  );

  const form = useValidatedForm<Field>(
    config,
    { name: "", email: "", password: "", passwordConfirm: "" },
    FIELD_ORDER,
  );
  const [terms, setTerms] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  function setPassword(value: string) {
    setValues({ password: value });
    form.setValue("password", value);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError("");
    setTermsError("");
    const valid = form.submit();
    if (!terms) setTermsError("Bạn cần đồng ý với điều khoản để tiếp tục.");
    if (!valid || !terms) return;
    if (turnstile.enabled && !turnstile.token) {
      setFormError("Hoàn tất bước xác minh chống bot rồi thử lại.");
      return;
    }
    const email = form.values.email.trim();
    const password = form.values.password;
    setSubmitting(true);
    try {
      await api<MessageResponse>("/auth/register", {
        method: "POST",
        body: {
          name: form.values.name.trim(),
          email,
          password,
          turnstile_token: turnstile.token,
          // Chỉ gửi mã đã tra ra người giới thiệu: backend trả 404 cho mã sai,
          // gửi bừa là chặn luôn việc đăng ký vì một thứ khách không biết.
          referral_code: referrer ? referralCode : "",
        },
      });
    } catch (error) {
      setFormError(errorMessage(error));
      turnstile.reset();
      setSubmitting(false);
      return;
    }
    // Từ 11/08/2026 (de876dc) backend không gửi email xác minh nữa: tài khoản
    // dùng được ngay, nên đăng nhập luôn. Đăng nhập hỏng thì tài khoản vẫn còn —
    // chỉ mời sang trang đăng nhập, không báo là đăng ký thất bại.
    try {
      const session = await api<LoginResponse>("/extension/auth/login", {
        method: "POST",
        body: { email, password, remember: false, installation_key: null, channel: "web" },
      });
      writeToken(session.token);
      navigate("/member");
    } catch {
      setDone("Tài khoản đã tạo xong. Đăng nhập để bắt đầu dùng FaceGo.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return <ResultPage title="Tài khoản đã sẵn sàng" detail={done} actionLabel="Đăng nhập" actionHref="/login" />;
  }

  return (
    <PublicShell
      title="Tạo tài khoản"
      description="Tạo xong là dùng được ngay, không cần xác minh email."
      footer={
        <Box textAlign="center">
          <SpaceBetween size="xs">
            <Box variant="span" color="text-body-secondary">
              Đã có tài khoản?{" "}
              <Link href="/login" onFollow={follow}>
                Đăng nhập
              </Link>
            </Box>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
      <GoogleSignInButton referralCode={referrer ? referralCode : ""} />
      <form onSubmit={handleSubmit}>
        <Form
          errorText={formError || undefined}
          errorIconAriaLabel="Lỗi"
          actions={
            <Button variant="primary" loading={submitting} loadingText="Đang tạo tài khoản">
              Tạo tài khoản
            </Button>
          }
        >
          <SpaceBetween size="l">
            {referrer && (
              <Alert type="success" statusIconAriaLabel="Thông tin" header={`${referrer.name} đã giới thiệu bạn`}>
                Mã <b>{referrer.code}</b> sẽ được gắn vào tài khoản mới và giảm{" "}
                {referrer.discount_percent ?? 10}% cho đơn đầu tiên của bạn.
              </Alert>
            )}
            <FormField label="Họ và tên" errorText={form.errors.name}>
              <Input
                ref={form.refFor("name")}
                autoComplete="name"
                placeholder="Nguyễn Minh Anh"
                value={form.values.name}
                onChange={({ detail }) => form.setValue("name", detail.value)}
                onBlur={() => form.handleBlur("name")}
              />
            </FormField>
            <FormField label="Email" constraintText={EMAIL_CONSTRAINT} errorText={form.errors.email}>
              <Input
                ref={form.refFor("email")}
                type="email"
                autoComplete="email"
                placeholder="ban@example.com"
                value={form.values.email}
                onChange={({ detail }) => form.setValue("email", detail.value)}
                onBlur={() => form.handleBlur("email")}
              />
            </FormField>
            <FormField label="Mật khẩu" constraintText={PASSWORD_CONSTRAINT} errorText={form.errors.password}>
              <Input
                ref={form.refFor("password")}
                type="password"
                autoComplete="new-password"
                value={form.values.password}
                onChange={({ detail }) => setPassword(detail.value)}
                onBlur={() => form.handleBlur("password")}
              />
            </FormField>
            <FormField label="Nhập lại mật khẩu" errorText={form.errors.passwordConfirm}>
              <Input
                ref={form.refFor("passwordConfirm")}
                type="password"
                autoComplete="new-password"
                value={form.values.passwordConfirm}
                onChange={({ detail }) => form.setValue("passwordConfirm", detail.value)}
                onBlur={() => form.handleBlur("passwordConfirm")}
              />
            </FormField>
            {turnstile.enabled && (
              <FormField label="Xác minh chống bot">
                <div ref={turnstile.containerRef} />
              </FormField>
            )}
            <FormField errorText={termsError || undefined}>
              <Checkbox
                checked={terms}
                onChange={({ detail }) => {
                  setTerms(detail.checked);
                  if (detail.checked) setTermsError("");
                }}
              >
                Tôi đồng ý với{" "}
                <Link href="/terms" onFollow={follow}>
                  điều khoản sử dụng
                </Link>{" "}
                và{" "}
                <Link href="/privacy" onFollow={follow}>
                  chính sách bảo mật
                </Link>
              </Checkbox>
            </FormField>
          </SpaceBetween>
        </Form>
      </form>
      </SpaceBetween>
    </PublicShell>
  );
}
